import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { HugeiconsIcon } from '@hugeicons/react'
import { Search01Icon } from '@hugeicons/core-free-icons'

import { cn } from '@/lib/utils'

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    data-slot='command'
    className={cn(
      'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
      className
    )}
    {...props}
  />
))
Command.displayName = 'Command'

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div
    data-slot='command-input-wrapper'
    className='border-border/50 flex items-center gap-2 border-b px-2'
  >
    <HugeiconsIcon
      icon={Search01Icon}
      strokeWidth={2}
      className='size-3.5 text-muted-foreground shrink-0'
    />
    <CommandPrimitive.Input
      ref={ref}
      data-slot='command-input'
      className={cn(
        'placeholder:text-muted-foreground flex h-8 w-full rounded-md bg-transparent py-2 text-xs/relaxed outline-none disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    />
  </div>
))
CommandInput.displayName = 'CommandInput'

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    data-slot='command-list'
    className={cn(
      'max-h-64 scroll-py-1 overflow-y-auto overflow-x-hidden',
      className
    )}
    {...props}
  />
))
CommandList.displayName = 'CommandList'

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    data-slot='command-empty'
    className='text-muted-foreground py-4 text-center text-xs'
    {...props}
  />
))
CommandEmpty.displayName = 'CommandEmpty'

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    data-slot='command-group'
    className={cn(
      'text-popover-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs',
      className
    )}
    {...props}
  />
))
CommandGroup.displayName = 'CommandGroup'

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    data-slot='command-separator'
    className={cn('bg-border/50 -mx-1 my-1 h-px', className)}
    {...props}
  />
))
CommandSeparator.displayName = 'CommandSeparator'

// Hack to ensure that cmdk's auto-scroll doesn't misbehave when we're in an iframe
// cmdk's auto-scroll was scrolling the actual iframe parent and jumping the whole page
// We scope it to the list height to avoid that
function containScrollWithinList(el: HTMLElement) {
  const list = el.closest<HTMLElement>('[data-slot="command-list"]')
  if (!list) return
  const elRect = el.getBoundingClientRect()
  const listRect = list.getBoundingClientRect()
  if (elRect.top < listRect.top) {
    list.scrollTop -= listRect.top - elRect.top
  } else if (elRect.bottom > listRect.bottom) {
    list.scrollTop += elRect.bottom - listRect.bottom
  }
}

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => {
  const setRef = React.useCallback(
    (node: React.ElementRef<typeof CommandPrimitive.Item> | null) => {
      if (node) node.scrollIntoView = () => containScrollWithinList(node)
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )
  return (
    <CommandPrimitive.Item
      ref={setRef}
      data-slot='command-item'
      className={cn(
        "min-h-7 outline-hidden data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1 text-xs/relaxed data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    />
  )
})
CommandItem.displayName = 'CommandItem'

export {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
}
